import getConfiguration from '@/store/getConfiguration';
import {
  NotificationApi,
  GetNotificationsOutputDTO,
  CreateNotificationRequest,
  UpdateNotificationRequest,
} from '@/api';
import { showError, showSuccess } from '@/mixins/toastService';

async function getApi() {
  const configuration = await getConfiguration();
  return new NotificationApi(configuration, '');
}

type InitialState = {
  notifications: GetNotificationsOutputDTO;
  isLoadingNotifications: boolean;
};

export const initialState = (): InitialState => ({
  notifications: { notifications: [] },
  isLoadingNotifications: false,
});
export type DockNotificationState = ReturnType<typeof initialState>;
type ActionType = {
  commit: Function;
  state: DockNotificationState;
};

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    resetState(state: DockNotificationState) {
      Object.assign(state, initialState());
    },

    setNotifications(
      state: DockNotificationState,
      notifications: GetNotificationsOutputDTO,
    ) {
      state.notifications = notifications;
    },
    setLoadingNotifications(state: DockNotificationState, isLoading: boolean) {
      state.isLoadingNotifications = isLoading;
    },
  },
  actions: {
    async fetchNotifications({ commit }: ActionType, locationId: string) {
      try {
        commit('setLoadingNotifications', true);
        const api = await getApi();
        const { data } =
          await api.getNotificationsDockschedulingNotificationsGet(locationId);
        commit('setNotifications', data);
      } catch (error) {
        showError('dock-management.error-fetching-notifications');
      } finally {
        commit('setLoadingNotifications', false);
      }
    },
    async removeNotification({ commit }: ActionType, notificationId: string) {
      try {
        commit('setLoadingNotifications', true);
        const api = await getApi();
        await api.deleteNotificationDockschedulingNotificationsNotificationIdDelete(
          notificationId,
        );
        showSuccess('dock-management.success-deleting-notification');
      } catch (error) {
        showError('dock-management.error-deleting-notification');
        throw error;
      } finally {
        commit('setLoadingNotifications', false);
      }
    },

    async createNotification(
      { commit }: ActionType,
      payload: CreateNotificationRequest,
    ) {
      try {
        commit('setLoadingNotifications', true);
        const api = await getApi();
        await api.createNotificationDockschedulingNotificationsPost(payload);
        showSuccess('dock-management.success-creating-notification');
      } catch (error) {
        showError('dock-management.error-creating-notification');
        throw error;
      } finally {
        commit('setLoadingNotifications', false);
      }
    },

    async updateNotification(
      { commit }: ActionType,
      payload: UpdateNotificationRequest & { id: string },
    ) {
      try {
        commit('setLoadingNotifications', true);
        const api = await getApi();
        if (!payload.id) {
          throw new Error('Notification ID is required for update');
        }
        await api.updateNotificationDockschedulingNotificationsNotificationIdPut(
          payload.id,
          payload,
        );
        showSuccess('dock-management.success-updating-notification');
      } catch (error) {
        showError('dock-management.error-updating-notification');
        throw error;
      } finally {
        commit('setLoadingNotifications', false);
      }
    },
  },
};
